import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { invoicesAPI, bookingsAPI, venuesAPI } from '../services/api';
import { formatCurrency, formatDate } from '../utils/currency';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { ImageCropDialog } from '../components/ImageCropDialog';
import usePermissions from '../hooks/usePermissions';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import InvoiceSettingsDialog from '../components/invoices/InvoiceSettingsDialog';
import { 
  generateInvoicePDF, 
  generateBulkPDF, 
  fixImageUrl 
} from '../utils/invoicePdfGenerator';
import { 
  Plus, Search, FileText, Edit, Trash2, Filter, Download,
  Calendar, Send, Check, X, Share2, Copy, Mail, MessageCircle,
  Building2, User, Phone, MapPin, CreditCard, Landmark, Settings,
  RefreshCw, Eye, ChevronDown, Link, Printer, IndianRupee, Receipt,
  FileSpreadsheet, AlertCircle, Upload, QrCode, Loader2, Image as ImageIcon, Lock
} from 'lucide-react';

const STATUS_COLORS = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  sent: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  overdue: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
};

const DEFAULT_UNITS = ['nos', 'hrs', 'days', 'sqft', 'kg', 'liters', 'pcs', 'set'];

const InvoicesModuleDisabledScreen = () => (
  <div className="min-h-[70vh] flex items-center justify-center p-4">
    <Card className="max-w-lg w-full border-2 border-dashed border-orange-200 dark:border-orange-900">
      <CardContent className="p-8 text-center space-y-4">
        <div className="mx-auto w-16 h-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
          <Lock className="w-8 h-8 text-orange-600 dark:text-orange-400" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-foreground">
          Invoices Module is Disabled
        </h2>
        <p className="text-sm text-gray-600 dark:text-muted-foreground">
          This feature is currently not activated for your account.
          Please ask the Software Developer / MyVenueMate Admin to enable the <b>Invoices</b> module from the Master Control Panel.
        </p>
        <div className="text-xs text-gray-500 dark:text-muted-foreground pt-2 border-t dark:border-border">
          Contact: <span className="font-medium">support@myvenuemate.in</span>
        </div>
      </CardContent>
    </Card>
  </div>
);

const Invoices = () => {
  const { currentUser } = useAuth();
  const enabledModules = currentUser?.enabledModules;
  const moduleEnabled = !!currentUser?.isMaster
    || (Array.isArray(enabledModules) && enabledModules.includes('invoices'));

  // State
  const [invoices, setInvoices] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [venues, setVenues] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [bulkDownloading, setBulkDownloading] = useState(false);
  
  // Booking search state
  const [bookingSearchTerm, setBookingSearchTerm] = useState('');
  const [bookingSearchResults, setBookingSearchResults] = useState([]);
  const [showBookingDropdown, setShowBookingDropdown] = useState(false);
  const bookingSearchRef = React.useRef(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  
  // Selected items
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVenue, setFilterVenue] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  // Invoice Settings State
  const [invoiceSettings, setInvoiceSettings] = useState({
    bankDetails: { bank_name: '', account_name: '', account_number: '', ifsc_code: '', upi_id: '' },
    paymentQrCodeUrl: '',
    companyDetails: { name: '', address: '', phone: '', email: '', gstin: '', logo_url: '' },
    signature: { url: '', name: '', designation: '', showOnInvoice: true },
    defaultTermsConditions: '',
    defaultNotes: '',
    invoicePrefix: 'INV',
    gstDefaults: { enabled: false, gst_type: 'cgst_sgst', cgst_rate: 9, sgst_rate: 9, igst_rate: 18 },
    customUnits: [],
    pdfTemplate: { borderDesign: 'botanical', colorTheme: 'sage_cream' }
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [uploadingField, setUploadingField] = useState(null); // Track which field is uploading (logo, qr, signature)
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageSrc, setCropImageSrc] = useState(null);
  const [cropCategory, setCropCategory] = useState(null);
  const [newCustomUnit, setNewCustomUnit] = useState(''); // For adding new custom units
  
  // Form state
  const [formData, setFormData] = useState({
    booking_id: '',
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    customer_address: '',
    customer_gstin: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    venue_id: '',
    venue_name: '',
    venue_logo_url: '',
    line_items: [{ service: '', hsn_sac: '', unit: 'nos', unit_price: 0, quantity: 1, amount: 0 }],
    gst_config: { enabled: false, gst_type: 'cgst_sgst', cgst_rate: 9, sgst_rate: 9, igst_rate: 18 },
    bank_details: { bank_name: '', account_name: '', account_number: '', ifsc_code: '', upi_id: '' },
    terms_conditions: '',
    notes: '',
    discount_percent: 0
  });
  
  // Share state
  const [shareData, setShareData] = useState({ link: '', copied: false });
  
  const { toast } = useToast();
  const { canViewInvoices, canEditInvoices, canCreateInvoices, isAdmin } = usePermissions();
  const location = useLocation();
  const navigateTo = useNavigate();

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterVenue !== 'all') params.venueId = filterVenue;
      if (dateFrom) params.startDate = dateFrom;
      if (dateTo) params.endDate = dateTo;
      
      const [invoicesRes, bookingsRes, venuesRes, summaryRes] = await Promise.all([
        invoicesAPI.getAll(params),
        // Auxiliary dropdown data - degrade gracefully for staff without those permissions
        bookingsAPI.getAll().catch(() => []),
        venuesAPI.getAll().catch(() => []),
        invoicesAPI.getSummary().catch(() => null)
      ]);
      
      setInvoices(invoicesRes.invoices || []);
      setBookings(bookingsRes || []);
      setVenues(venuesRes || []);
      setSummary(summaryRes);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({ title: 'Error', description: 'Failed to load invoices', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filterStatus, filterVenue, dateFrom, dateTo, toast]);

  useEffect(() => {
    if (moduleEnabled) loadData();
  }, [loadData, moduleEnabled]);

  // Load templates
  const loadTemplates = useCallback(async () => {
    try {
      const templatesRes = await invoicesAPI.getTemplates();
      setTemplates(templatesRes || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  }, []);

  useEffect(() => {
    if (moduleEnabled) loadTemplates();
  }, [loadTemplates, moduleEnabled]);

  // Load invoice settings
  const loadInvoiceSettings = useCallback(async () => {
    try {
      const settingsRes = await invoicesAPI.getSettings();
      setInvoiceSettings(settingsRes);
    } catch (error) {
      console.error('Failed to load invoice settings:', error);
    }
  }, []);

  useEffect(() => {
    if (moduleEnabled) loadInvoiceSettings();
  }, [loadInvoiceSettings, moduleEnabled]);

  // Auto-open Settings dialog when navigated to with state.openSettings (e.g. from Quotations page)
  useEffect(() => {
    if (moduleEnabled && location.state?.openSettings) {
      setSettingsDialogOpen(true);
    }
  }, [location.state, moduleEnabled]);

  // Handle navigation from Bookings page (Create Invoice for a payment)
  useEffect(() => {
    if (location.state?.createFromBooking) {
      const s = location.state;
      const getNextNumber = async () => {
        try {
          const res = await invoicesAPI.getNextNumber();
          setFormData(prev => ({
            ...prev,
            invoice_number: res.nextNumber || '',
            booking_id: s.bookingId || '',
            customer_name: s.customerName || '',
            customer_phone: s.customerPhone || '',
            customer_email: s.customerEmail || '',
            venue_id: s.venueId || '',
            venue_name: s.venueName || '',
            invoice_date: s.paymentDate || new Date().toISOString().split('T')[0],
            line_items: [{
              service: s.serviceName || 'Event Service',
              hsn_sac: '',
              unit: 'nos',
              unit_price: Number(s.paymentAmount) || 0,
              quantity: 1,
              amount: Number(s.paymentAmount) || 0
            }],
            notes: s.paymentNote || '',
          }));
          setCreateDialogOpen(true);
        } catch (err) {
          console.error('Failed to get next number:', err);
        }
      };
      getNextNumber();
      // Clear the state so refreshing doesn't re-trigger
      navigateTo(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigateTo, location.pathname]);

  // Save invoice settings
  const saveInvoiceSettings = async () => {
    setSettingsLoading(true);
    try {
      await invoicesAPI.updateSettings({
        bank_name: invoiceSettings.bankDetails?.bank_name || '',
        account_name: invoiceSettings.bankDetails?.account_name || '',
        account_number: invoiceSettings.bankDetails?.account_number || '',
        ifsc_code: invoiceSettings.bankDetails?.ifsc_code || '',
        upi_id: invoiceSettings.bankDetails?.upi_id || '',
        payment_qr_code_url: invoiceSettings.paymentQrCodeUrl || '',
        company_name: invoiceSettings.companyDetails?.name || '',
        company_address: invoiceSettings.companyDetails?.address || '',
        company_phone: invoiceSettings.companyDetails?.phone || '',
        company_email: invoiceSettings.companyDetails?.email || '',
        company_gstin: invoiceSettings.companyDetails?.gstin || '',
        company_logo_url: invoiceSettings.companyDetails?.logo_url || '',
        signature_url: invoiceSettings.signature?.url || '',
        signatory_name: invoiceSettings.signature?.name || '',
        signatory_designation: invoiceSettings.signature?.designation || '',
        show_signature_on_invoice: invoiceSettings.signature?.showOnInvoice !== false,
        default_terms_conditions: invoiceSettings.defaultTermsConditions || '',
        default_notes: invoiceSettings.defaultNotes || '',
        invoice_prefix: invoiceSettings.invoicePrefix || 'INV',
        default_gst_enabled: invoiceSettings.gstDefaults?.enabled || false,
        default_gst_type: invoiceSettings.gstDefaults?.gst_type || 'cgst_sgst',
        default_cgst_rate: invoiceSettings.gstDefaults?.cgst_rate || 9,
        default_sgst_rate: invoiceSettings.gstDefaults?.sgst_rate || 9,
        default_igst_rate: invoiceSettings.gstDefaults?.igst_rate || 18,
        custom_units: invoiceSettings.customUnits || [],
        pdf_border_design: invoiceSettings.pdfTemplate?.borderDesign || 'botanical',
        pdf_color_theme: invoiceSettings.pdfTemplate?.colorTheme || 'sage_cream'
      });
      toast({ title: 'Success', description: 'Invoice settings saved successfully' });
      setSettingsDialogOpen(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSettingsLoading(false);
    }
  };

  // Add custom unit
  const addCustomUnit = () => {
    const unit = newCustomUnit.trim().toLowerCase();
    if (!unit) return;
    
    // Check if already exists in default or custom units
    if (DEFAULT_UNITS.includes(unit) || invoiceSettings.customUnits?.includes(unit)) {
      toast({ title: 'Error', description: 'This unit already exists', variant: 'destructive' });
      return;
    }
    
    setInvoiceSettings(prev => ({
      ...prev,
      customUnits: [...(prev.customUnits || []), unit]
    }));
    setNewCustomUnit('');
  };

  // Remove custom unit
  const removeCustomUnit = (unitToRemove) => {
    setInvoiceSettings(prev => ({
      ...prev,
      customUnits: (prev.customUnits || []).filter(u => u !== unitToRemove)
    }));
  };

  // Get all available units (default + custom)
  const getAllUnits = () => {
    return [...DEFAULT_UNITS, ...(invoiceSettings.customUnits || [])];
  };

  // Handle image selection — opens crop dialog
  const handleSettingsImageSelect = (e, fieldType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Error', description: 'Please select an image file', variant: 'destructive' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Error', description: 'Image size must be less than 5MB', variant: 'destructive' });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCropImageSrc(reader.result);
      setCropCategory(fieldType);
      setCropDialogOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = ''; // Reset input so same file can be re-selected
  };

  // Handle cropped image upload
  const handleCroppedImageUpload = async (croppedFile) => {
    const fieldType = cropCategory;
    setUploadingField(fieldType);
    try {
      const result = await invoicesAPI.uploadSettingsImage(croppedFile, fieldType);
      if (result.success && result.path) {
        const imageUrl = invoicesAPI.getSettingsImageUrl(result.path);
        
        // Update local state and auto-save to database
        let updatedSettings = { ...invoiceSettings };
        if (fieldType === 'logo') {
          updatedSettings = { ...updatedSettings, companyDetails: { ...updatedSettings.companyDetails, logo_url: imageUrl } };
        } else if (fieldType === 'qr') {
          updatedSettings = { ...updatedSettings, paymentQrCodeUrl: imageUrl };
        } else if (fieldType === 'signature') {
          updatedSettings = { ...updatedSettings, signature: { ...updatedSettings.signature, url: imageUrl } };
        }
        
        setInvoiceSettings(updatedSettings);
        
        // Auto-save to database immediately after image upload
        try {
          await invoicesAPI.updateSettings({
            bank_name: updatedSettings.bankDetails?.bank_name || '',
            account_name: updatedSettings.bankDetails?.account_name || '',
            account_number: updatedSettings.bankDetails?.account_number || '',
            ifsc_code: updatedSettings.bankDetails?.ifsc_code || '',
            upi_id: updatedSettings.bankDetails?.upi_id || '',
            payment_qr_code_url: updatedSettings.paymentQrCodeUrl || '',
            company_name: updatedSettings.companyDetails?.name || '',
            company_address: updatedSettings.companyDetails?.address || '',
            company_phone: updatedSettings.companyDetails?.phone || '',
            company_email: updatedSettings.companyDetails?.email || '',
            company_gstin: updatedSettings.companyDetails?.gstin || '',
            company_logo_url: updatedSettings.companyDetails?.logo_url || '',
            signature_url: updatedSettings.signature?.url || '',
            signatory_name: updatedSettings.signature?.name || '',
            signatory_designation: updatedSettings.signature?.designation || '',
            show_signature_on_invoice: updatedSettings.signature?.showOnInvoice !== false,
            default_terms_conditions: updatedSettings.defaultTermsConditions || '',
            default_notes: updatedSettings.defaultNotes || '',
            invoice_prefix: updatedSettings.invoicePrefix || 'INV',
            default_gst_enabled: updatedSettings.gstDefaults?.enabled || false,
            default_gst_type: updatedSettings.gstDefaults?.gst_type || 'cgst_sgst',
            default_cgst_rate: updatedSettings.gstDefaults?.cgst_rate || 9,
            default_sgst_rate: updatedSettings.gstDefaults?.sgst_rate || 9,
            default_igst_rate: updatedSettings.gstDefaults?.igst_rate || 18,
            custom_units: updatedSettings.customUnits || [],
            pdf_border_design: updatedSettings.pdfTemplate?.borderDesign || 'botanical',
            pdf_color_theme: updatedSettings.pdfTemplate?.colorTheme || 'sage_cream'
          });
          toast({ title: 'Success', description: 'Image uploaded and saved' });
        } catch (saveError) {
          console.error('Auto-save error:', saveError);
          toast({ title: 'Warning', description: 'Image uploaded but failed to auto-save. Please click Save Settings.', variant: 'destructive' });
        }
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Error', description: error.message || 'Failed to upload image', variant: 'destructive' });
    } finally {
      setUploadingField(null);
    }
  };

  // Filter bookings based on search term
  useEffect(() => {
    if (bookingSearchTerm.length >= 2) {
      const filtered = bookings.filter(b => 
        b.customerName?.toLowerCase().includes(bookingSearchTerm.toLowerCase()) ||
        b.customerPhone?.includes(bookingSearchTerm) ||
        b.eventType?.toLowerCase().includes(bookingSearchTerm.toLowerCase())
      ).slice(0, 10); // Limit to 10 results
      setBookingSearchResults(filtered);
      setShowBookingDropdown(filtered.length > 0);
    } else {
      setBookingSearchResults([]);
      setShowBookingDropdown(false);
    }
  }, [bookingSearchTerm, bookings]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bookingSearchRef.current && !bookingSearchRef.current.contains(event.target)) {
        setShowBookingDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset form - use saved settings as defaults
  const resetForm = async () => {
    try {
      const nextNum = await invoicesAPI.getNextNumber();
      setFormData({
        booking_id: '',
        customer_name: '',
        customer_phone: '',
        customer_email: '',
        customer_address: '',
        customer_gstin: '',
        invoice_number: nextNum.nextNumber || '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        venue_id: '',
        venue_name: '',
        venue_logo_url: invoiceSettings.companyDetails?.logo_url || '',
        line_items: [{ service: '', hsn_sac: '', unit: 'nos', unit_price: 0, quantity: 1, amount: 0 }],
        gst_config: { 
          enabled: invoiceSettings.gstDefaults?.enabled || false, 
          gst_type: invoiceSettings.gstDefaults?.gst_type || 'cgst_sgst', 
          cgst_rate: invoiceSettings.gstDefaults?.cgst_rate || 9, 
          sgst_rate: invoiceSettings.gstDefaults?.sgst_rate || 9, 
          igst_rate: invoiceSettings.gstDefaults?.igst_rate || 18 
        },
        bank_details: { 
          bank_name: invoiceSettings.bankDetails?.bank_name || '', 
          account_name: invoiceSettings.bankDetails?.account_name || '', 
          account_number: invoiceSettings.bankDetails?.account_number || '', 
          ifsc_code: invoiceSettings.bankDetails?.ifsc_code || '', 
          upi_id: invoiceSettings.bankDetails?.upi_id || '' 
        },
        terms_conditions: invoiceSettings.defaultTermsConditions || '',
        notes: invoiceSettings.defaultNotes || '',
        discount_percent: 0
      });
      setBookingSearchTerm('');
      setBookingSearchResults([]);
      setShowBookingDropdown(false);
    } catch (error) {
      console.error('Failed to get next invoice number:', error);
    }
  };

  // Fetch customer from booking (when user selects from autocomplete)
  const handleBookingSelect = async (booking) => {
    if (!booking) return;
    
    try {
      const customerData = await invoicesAPI.fetchCustomerFromBooking(booking.id);
      setFormData(prev => ({
        ...prev,
        booking_id: booking.id,
        customer_name: customerData.customerName || '',
        customer_phone: customerData.customerPhone || '',
        customer_email: customerData.customerEmail || '',
        venue_id: customerData.venueId || '',
        venue_name: customerData.venueName || ''
      }));
      // Update search field to show selected customer
      setBookingSearchTerm(`${booking.customerName} - ${booking.eventType}`);
      setShowBookingDropdown(false);
      toast({ title: 'Customer Loaded', description: `Details loaded from ${booking.eventType} booking` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch customer details', variant: 'destructive' });
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    formData.line_items.forEach(item => {
      subtotal += (item.unit_price || 0) * (item.quantity || 1);
    });
    
    const discountAmount = subtotal * (formData.discount_percent / 100);
    const taxableAmount = subtotal - discountAmount;
    
    let cgst = 0, sgst = 0, igst = 0;
    if (formData.gst_config.enabled) {
      if (formData.gst_config.gst_type === 'igst') {
        igst = taxableAmount * (formData.gst_config.igst_rate / 100);
      } else {
        cgst = taxableAmount * (formData.gst_config.cgst_rate / 100);
        sgst = taxableAmount * (formData.gst_config.sgst_rate / 100);
      }
    }
    
    const totalTax = cgst + sgst + igst;
    const totalBeforeRoundOff = taxableAmount + totalTax;
    
    // Round off to nearest rupee
    const roundedTotal = Math.round(totalBeforeRoundOff);
    const roundOffAmount = roundedTotal - totalBeforeRoundOff;
    
    return { 
      subtotal, 
      discountAmount, 
      taxableAmount, 
      cgst, 
      sgst, 
      igst, 
      totalTax, 
      grandTotal: roundedTotal,
      roundOffAmount 
    };
  };

  // Add line item
  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, { service: '', hsn_sac: '', unit: 'nos', unit_price: 0, quantity: 1, amount: 0 }]
    }));
  };

  // Remove line item
  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  // Update line item
  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // If user edited 'amount' directly (for reverse GST calculation)
      if (field === 'amount') {
        const enteredAmount = parseFloat(value) || 0;
        const qty = newItems[index].quantity || 1;
        
        // If GST is enabled, reverse-calculate the unit price from the entered amount (inclusive of GST)
        if (prev.gst_config?.enabled) {
          let gstRate = 0;
          if (prev.gst_config.gst_type === 'igst') {
            gstRate = prev.gst_config.igst_rate || 18;
          } else {
            gstRate = (prev.gst_config.cgst_rate || 9) + (prev.gst_config.sgst_rate || 9);
          }
          // Amount entered is inclusive of GST, so reverse calculate
          const baseAmount = enteredAmount / (1 + gstRate / 100);
          newItems[index].unit_price = Math.round((baseAmount / qty) * 100) / 100;
        } else {
          // No GST - amount is simply unit_price * qty
          newItems[index].unit_price = Math.round((enteredAmount / qty) * 100) / 100;
        }
        newItems[index].amount = enteredAmount;
      } else {
        // For other fields, calculate amount normally
        newItems[index].amount = (newItems[index].unit_price || 0) * (newItems[index].quantity || 1);
      }
      
      return { ...prev, line_items: newItems };
    });
  };

  // Submit invoice
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_name || formData.line_items.length === 0) {
      toast({ title: 'Error', description: 'Please fill customer name and add at least one item', variant: 'destructive' });
      return;
    }
    
    // Check if at least one line item has a service name
    const hasValidItem = formData.line_items.some(item => item.service && item.service.trim() !== '');
    if (!hasValidItem) {
      toast({ title: 'Error', description: 'Please add at least one service item', variant: 'destructive' });
      return;
    }
    
    // Calculate totals including round off
    const calculatedTotals = calculateTotals();
    
    // Use bank details from settings
    const submitData = {
      ...formData,
      round_off_amount: calculatedTotals.roundOffAmount,
      grand_total: calculatedTotals.grandTotal,
      bank_details: {
        bank_name: invoiceSettings.bankDetails?.bank_name || '',
        account_name: invoiceSettings.bankDetails?.account_name || '',
        account_number: invoiceSettings.bankDetails?.account_number || '',
        ifsc_code: invoiceSettings.bankDetails?.ifsc_code || '',
        upi_id: invoiceSettings.bankDetails?.upi_id || ''
      }
    };
    
    try {
      if (selectedInvoice) {
        await invoicesAPI.update(selectedInvoice.id, submitData);
        toast({ title: 'Success', description: 'Invoice updated successfully' });
      } else {
        await invoicesAPI.create(submitData);
        toast({ title: 'Success', description: 'Invoice created successfully' });
      }
      setCreateDialogOpen(false);
      setSelectedInvoice(null);
      loadData();
    } catch (error) {
      // Check for demo account restriction
      const errorMessage = error.message || 'Failed to save invoice';
      if (errorMessage.toLowerCase().includes('demo') || errorMessage.toLowerCase().includes('read-only')) {
        toast({ 
          title: 'Demo Account Restricted', 
          description: 'Demo account cannot create invoices. Please use: testuser@myvenuemate.in / Test@2025', 
          variant: 'destructive' 
        });
      } else {
        toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
      }
    }
  };

  // Delete invoice
  const handleDelete = async () => {
    if (!invoiceToDelete) return;
    
    try {
      await invoicesAPI.delete(invoiceToDelete.id);
      toast({ title: 'Success', description: 'Invoice deleted' });
      setDeleteConfirmOpen(false);
      setInvoiceToDelete(null);
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete invoice', variant: 'destructive' });
    }
  };

  // Edit invoice
  const handleEdit = (invoice) => {
    setSelectedInvoice(invoice);
    setFormData({
      booking_id: invoice.bookingId || '',
      customer_name: invoice.customerName || '',
      customer_phone: invoice.customerPhone || '',
      customer_email: invoice.customerEmail || '',
      customer_address: invoice.customerAddress || '',
      customer_gstin: invoice.customerGstin || '',
      invoice_number: invoice.invoiceNumber || '',
      invoice_date: invoice.invoiceDate?.split('T')[0] || '',
      due_date: invoice.dueDate?.split('T')[0] || '',
      venue_id: invoice.venueId || '',
      venue_name: invoice.venueName || '',
      venue_logo_url: invoice.venueLogoUrl || '',
      line_items: invoice.lineItems || [{ service: '', hsn_sac: '', unit: 'nos', unit_price: 0, quantity: 1 }],
      gst_config: invoice.gstConfig || { enabled: false, gst_type: 'cgst_sgst', cgst_rate: 9, sgst_rate: 9, igst_rate: 18 },
      bank_details: invoice.bankDetails || { bank_name: '', account_name: '', account_number: '', ifsc_code: '', upi_id: '' },
      terms_conditions: invoice.termsConditions || '',
      notes: invoice.notes || '',
      discount_percent: invoice.discountPercent || 0
    });
    setCreateDialogOpen(true);
  };

  // Helper function to load image as base64 data URL

  // Generate PDF - Using extracted utility.
  // Always refetch the latest invoice settings on demand so that the PDF
  // gets logo / QR / signature / bank info even if the user clicked download
  // before the on-mount settings fetch resolved (fresh-login race condition).
  const generatePDF = async (invoice) => {
    // Refetch fresh invoice settings on every download click.
    // This protects against the fresh-login race where the on-mount settings
    // fetch hasn't resolved yet when the user clicks Download, which would
    // otherwise produce a PDF without the logo / QR / signature / bank info.
    let settingsForPdf = invoiceSettings;
    try {
      const fresh = await invoicesAPI.getSettings();
      if (fresh) {
        settingsForPdf = fresh;
        setInvoiceSettings(fresh);
      }
    } catch (e) {
      // non-fatal: fall back to whatever is in state; generator tolerates empty fields
    }
    try {
      await generateInvoicePDF(invoice, settingsForPdf, () => {
        toast({ title: 'Success', description: 'PDF downloaded' });
      });
    } catch (err) {
      toast({ title: 'Error', description: err?.message || 'PDF generation failed', variant: 'destructive' });
    }
  };
  // Bulk download - combined PDF with all selected invoices
  const bulkDownloadPDF = async () => {
    const selected = invoices.filter(inv => selectedInvoiceIds.includes(inv.id));
    if (selected.length === 0) {
      toast({ title: 'No invoices selected', description: 'Please select at least one invoice', variant: 'destructive' });
      return;
    }
    
    setBulkDownloading(true);
    toast({ title: 'Generating...', description: `Preparing ${selected.length} invoice(s) as PDF` });
    
    // Refetch settings on-demand to avoid fresh-login race where in-state settings
    // are still the empty default when user clicks bulk download.
    let settingsForPdf = invoiceSettings;
    try {
      const fresh = await invoicesAPI.getSettings();
      if (fresh) { settingsForPdf = fresh; setInvoiceSettings(fresh); }
    } catch (_) { /* fallback to state */ }

    try {
      await generateBulkPDF(
        selected,
        settingsForPdf,
        null, // onProgress
        () => {
          toast({ title: 'Success', description: `${selected.length} invoice(s) downloaded` });
          setSelectedInvoiceIds([]);
        },
        (error) => {
          toast({ title: 'Error', description: 'Failed to generate bulk PDF', variant: 'destructive' });
        }
      );
    } catch (error) {
      console.error('Bulk PDF error:', error);
      toast({ title: 'Error', description: 'Failed to generate bulk PDF', variant: 'destructive' });
    } finally {
      setBulkDownloading(false);
    }
  };

  // Render a single invoice page into a jsPDF doc (reusable for single + bulk)
  const handleShare = async (invoice) => {
    try {
      const linkRes = await invoicesAPI.getShareLink(invoice.id);
      const fullLink = `${window.location.origin}/invoice/view/${linkRes.shareToken}`;
      setShareData({ ...linkRes, link: fullLink, invoice });
      setShareDialogOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to generate share link', variant: 'destructive' });
    }
  };

  // Copy link
  const copyLink = () => {
    navigator.clipboard.writeText(shareData.link);
    setShareData(prev => ({ ...prev, copied: true }));
    setTimeout(() => setShareData(prev => ({ ...prev, copied: false })), 2000);
  };

  // Share via WhatsApp
  const shareWhatsApp = async () => {
    try {
      const waRes = await invoicesAPI.getWhatsAppLink(shareData.invoice.id);
      const fullLink = `${window.location.origin}/invoice/view/${waRes.shareToken}`;
      const message = `${waRes.message}\n\nView Invoice: ${fullLink}`;
      
      if (navigator.share) {
        navigator.share({ title: 'Invoice', text: message, url: fullLink });
      } else {
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to share via WhatsApp', variant: 'destructive' });
    }
  };

  // Share via Email
  const shareEmail = () => {
    const subject = `Invoice ${shareData.invoice?.invoiceNumber}`;
    const body = `Please find your invoice:\n\nInvoice #: ${shareData.invoice?.invoiceNumber}\nAmount: Rs. ${shareData.invoice?.grand_total?.toLocaleString('en-IN')}\n\nView Invoice: ${shareData.link}`;
    window.location.href = `mailto:${shareData.invoice?.customerEmail || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  // Mark status
  const handleStatusChange = async (invoice, status) => {
    try {
      if (status === 'sent') await invoicesAPI.markSent(invoice.id);
      else if (status === 'paid') await invoicesAPI.markPaid(invoice.id);
      else if (status === 'cancelled') await invoicesAPI.markCancelled(invoice.id);
      
      toast({ title: 'Success', description: `Invoice marked as ${status}` });
      loadData();
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const totals = calculateTotals();

  // Permission check
  if (!canViewInvoices()) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="p-6 text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold">Access Restricted</h3>
          <p className="text-sm text-gray-500">You do not have permission to access invoices.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="invoices-page">
      {!moduleEnabled && <InvoicesModuleDisabledScreen />}
      {moduleEnabled && (
      <>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg sm:text-2xl font-bold text-teal-600 dark:text-primary flex items-center gap-1.5">
          <Receipt className="w-5 h-5 sm:w-6 sm:h-6 flex-shrink-0" />
          INVOICES
        </h1>
        
        <div className="flex gap-2">
          {/* Settings Button */}
          {canEditInvoices() && (
            <Button variant="outline" size="sm" onClick={() => setSettingsDialogOpen(true)} title="Invoice Settings">
              <Settings className="w-4 h-4" />
            </Button>
          )}
          
          <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          
          {canCreateInvoices() && (
            <Dialog open={createDialogOpen} onOpenChange={(open) => {
              setCreateDialogOpen(open);
              if (open && !selectedInvoice) resetForm();
              if (!open) setSelectedInvoice(null);
            }}>
              <DialogTrigger asChild>
                <Button className="bg-teal-600 hover:bg-teal-700" data-testid="create-invoice-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  <span className="hidden sm:inline">Create Invoice</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{selectedInvoice ? 'Edit Invoice' : 'Create Invoice'}</DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Invoice Number & Date */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Invoice Number</Label>
                      <Input
                        value={formData.invoice_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                        placeholder="Auto-generated"
                      />
                      <p className="text-xs text-gray-500 mt-1">Leave empty for auto-generation</p>
                    </div>
                    <div>
                      <Label>Invoice Date *</Label>
                      <Input
                        type="date"
                        value={formData.invoice_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, invoice_date: e.target.value }))}
                        required
                      />
                    </div>
                    <div>
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  {/* Fetch from Booking - Autocomplete Search */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg" ref={bookingSearchRef}>
                    <Label className="text-blue-700 dark:text-blue-400">Fetch Customer from Booking (Optional)</Label>
                    <div className="relative mt-2">
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                          value={bookingSearchTerm}
                          onChange={(e) => setBookingSearchTerm(e.target.value)}
                          onFocus={() => bookingSearchResults.length > 0 && setShowBookingDropdown(true)}
                          placeholder="Type customer name, phone, or event type..."
                          className="pl-10"
                        />
                        {bookingSearchTerm && (
                          <button
                            type="button"
                            onClick={() => {
                              setBookingSearchTerm('');
                              setFormData(prev => ({ ...prev, booking_id: '' }));
                            }}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      
                      {/* Autocomplete Dropdown */}
                      {showBookingDropdown && bookingSearchResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-secondary border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {bookingSearchResults.map(booking => (
                            <button
                              key={booking.id}
                              type="button"
                              onClick={() => handleBookingSelect(booking)}
                              className="w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 border-b last:border-b-0 flex items-center justify-between"
                            >
                              <div>
                                <p className="font-medium text-gray-900 dark:text-foreground">{booking.customerName}</p>
                                <p className="text-sm text-gray-500">
                                  {booking.eventType} • {formatDate(booking.eventDate)}
                                  {booking.venueName && ` • ${booking.venueName}`}
                                </p>
                              </div>
                              <div className="text-right text-xs text-gray-400">
                                {booking.customerPhone && <p>{booking.customerPhone}</p>}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {bookingSearchTerm.length >= 2 && bookingSearchResults.length === 0 && (
                        <p className="text-xs text-gray-500 mt-1">No matching bookings found</p>
                      )}
                      {bookingSearchTerm.length > 0 && bookingSearchTerm.length < 2 && (
                        <p className="text-xs text-gray-500 mt-1">Type at least 2 characters to search</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Customer Details */}
                  <div className="space-y-4">
                    <h3 className="font-medium flex items-center gap-2">
                      <User className="w-4 h-4" /> Customer Details
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Customer Name *</Label>
                        <Input
                          value={formData.customer_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                          required
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={formData.customer_phone}
                          onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={formData.customer_email}
                          onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                        />
                      </div>
                      <div>
                        <Label>GSTIN</Label>
                        <Input
                          value={formData.customer_gstin}
                          onChange={(e) => setFormData(prev => ({ ...prev, customer_gstin: e.target.value }))}
                          placeholder="e.g., 29ABCDE1234F1Z5"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Address</Label>
                      <Textarea
                        value={formData.customer_address}
                        onChange={(e) => setFormData(prev => ({ ...prev, customer_address: e.target.value }))}
                        rows={2}
                      />
                    </div>
                  </div>
                  
                  {/* Venue & Logo */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Venue</Label>
                      <Select 
                        value={formData.venue_id} 
                        onValueChange={(v) => {
                          const venue = venues.find(ven => ven.id === v);
                          setFormData(prev => ({ ...prev, venue_id: v, venue_name: venue?.name || '' }));
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select venue" />
                        </SelectTrigger>
                        <SelectContent>
                          {venues.map(v => (
                            <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Line Items */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4" /> Line Items
                      </h3>
                      <Button type="button" variant="outline" size="sm" onClick={addLineItem}>
                        <Plus className="w-4 h-4 mr-1" /> Add Item
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      {formData.line_items.map((item, index) => (
                        <div key={index} className="grid grid-cols-12 gap-2 items-end bg-gray-50 dark:bg-secondary p-3 rounded-lg">
                          <div className="col-span-12 md:col-span-3">
                            <Label className="text-xs">Service *</Label>
                            <Input
                              value={item.service}
                              onChange={(e) => updateLineItem(index, 'service', e.target.value)}
                              placeholder="Service name"
                              required
                            />
                          </div>
                          <div className="col-span-6 md:col-span-2">
                            <Label className="text-xs">HSN/SAC</Label>
                            <Input
                              value={item.hsn_sac}
                              onChange={(e) => updateLineItem(index, 'hsn_sac', e.target.value)}
                              placeholder="998599"
                            />
                          </div>
                          <div className="col-span-6 md:col-span-1">
                            <Label className="text-xs">Unit</Label>
                            <Select value={item.unit} onValueChange={(v) => updateLineItem(index, 'unit', v)}>
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {getAllUnits().map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-5 md:col-span-2">
                            <Label className="text-xs">Unit Price *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => updateLineItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              min="0"
                              required
                            />
                          </div>
                          <div className="col-span-4 md:col-span-1">
                            <Label className="text-xs">Qty *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 1)}
                              min="0.01"
                              required
                            />
                          </div>
                          <div className="col-span-3 md:col-span-2">
                            <Label className="text-xs">Amount {formData.gst_config?.enabled && <span className="text-[9px] text-teal-600">(incl. GST)</span>}</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={item.amount || (item.unit_price || 0) * (item.quantity || 1)}
                              onChange={(e) => updateLineItem(index, 'amount', parseFloat(e.target.value) || 0)}
                              min="0"
                              placeholder="Enter amount"
                              className="font-medium text-teal-600"
                            />
                          </div>
                          <div className="col-span-12 md:col-span-1">
                            {formData.line_items.length > 1 && (
                              <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(index)} className="text-red-500">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Discount & GST */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label>Discount (%)</Label>
                      <Input
                        type="number"
                        value={formData.discount_percent}
                        onChange={(e) => setFormData(prev => ({ ...prev, discount_percent: parseFloat(e.target.value) || 0 }))}
                        min="0"
                        max="100"
                      />
                    </div>
                    
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <Label>Enable GST</Label>
                        <Switch
                          checked={formData.gst_config.enabled}
                          onCheckedChange={(checked) => setFormData(prev => ({
                            ...prev,
                            gst_config: { ...prev.gst_config, enabled: checked }
                          }))}
                        />
                      </div>
                      
                      {formData.gst_config.enabled && (
                        <div className="space-y-3 pl-4 border-l-2 border-teal-500">
                          <div>
                            <Label>GST Type</Label>
                            <Select
                              value={formData.gst_config.gst_type}
                              onValueChange={(v) => setFormData(prev => ({
                                ...prev,
                                gst_config: { ...prev.gst_config, gst_type: v }
                              }))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="cgst_sgst">CGST + SGST (Same State)</SelectItem>
                                <SelectItem value="igst">IGST (Different State)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {formData.gst_config.gst_type === 'igst' ? (
                            <div>
                              <Label>IGST Rate (%)</Label>
                              <Input
                                type="number"
                                value={formData.gst_config.igst_rate}
                                onChange={(e) => setFormData(prev => ({
                                  ...prev,
                                  gst_config: { ...prev.gst_config, igst_rate: parseFloat(e.target.value) || 18 }
                                }))}
                              />
                            </div>
                          ) : (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label>CGST (%)</Label>
                                <Input
                                  type="number"
                                  value={formData.gst_config.cgst_rate}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    gst_config: { ...prev.gst_config, cgst_rate: parseFloat(e.target.value) || 9 }
                                  }))}
                                />
                              </div>
                              <div>
                                <Label>SGST (%)</Label>
                                <Input
                                  type="number"
                                  value={formData.gst_config.sgst_rate}
                                  onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    gst_config: { ...prev.gst_config, sgst_rate: parseFloat(e.target.value) || 9 }
                                  }))}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Totals Preview */}
                  <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 p-4 rounded-lg">
                    <h3 className="font-medium mb-3">Invoice Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Subtotal:</span>
                        <span>{formatCurrency(totals.subtotal)}</span>
                      </div>
                      {totals.discountAmount > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span>Discount ({formData.discount_percent}%):</span>
                          <span>- {formatCurrency(totals.discountAmount)}</span>
                        </div>
                      )}
                      {formData.gst_config.enabled && (
                        <>
                          {formData.gst_config.gst_type === 'igst' ? (
                            <div className="flex justify-between">
                              <span>IGST ({formData.gst_config.igst_rate}%):</span>
                              <span>{formatCurrency(totals.igst)}</span>
                            </div>
                          ) : (
                            <>
                              <div className="flex justify-between">
                                <span>CGST ({formData.gst_config.cgst_rate}%):</span>
                                <span>{formatCurrency(totals.cgst)}</span>
                              </div>
                              <div className="flex justify-between">
                                <span>SGST ({formData.gst_config.sgst_rate}%):</span>
                                <span>{formatCurrency(totals.sgst)}</span>
                              </div>
                            </>
                          )}
                        </>
                      )}
                      {totals.roundOffAmount !== 0 && (
                        <div className="flex justify-between text-gray-500">
                          <span>Round Off:</span>
                          <span>{totals.roundOffAmount > 0 ? '+' : ''}{formatCurrency(totals.roundOffAmount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-lg pt-2 border-t">
                        <span>Grand Total:</span>
                        <span className="text-teal-600">{formatCurrency(totals.grandTotal)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Terms & Notes */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>Terms & Conditions</Label>
                        {invoiceSettings.defaultTermsConditions && (
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              data-testid="use-default-terms"
                              className="rounded border-gray-300 w-3.5 h-3.5"
                              checked={formData.terms_conditions?.includes(invoiceSettings.defaultTermsConditions)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const existing = formData.terms_conditions?.trim();
                                  const defaults = invoiceSettings.defaultTermsConditions;
                                  setFormData(prev => ({
                                    ...prev,
                                    terms_conditions: existing ? `${defaults}\n${existing}` : defaults
                                  }));
                                } else {
                                  setFormData(prev => ({
                                    ...prev,
                                    terms_conditions: prev.terms_conditions?.replace(invoiceSettings.defaultTermsConditions, '').trim()
                                  }));
                                }
                              }}
                            />
                            <span className="text-[10px] text-gray-500 dark:text-muted-foreground">Use Default</span>
                          </label>
                        )}
                      </div>
                      <Textarea
                        value={formData.terms_conditions}
                        onChange={(e) => setFormData(prev => ({ ...prev, terms_conditions: e.target.value }))}
                        rows={4}
                        placeholder="Payment terms, cancellation policy, etc."
                      />
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <Label>Notes</Label>
                        {invoiceSettings.defaultNotes && (
                          <label className="flex items-center gap-1.5 cursor-pointer">
                            <input
                              type="checkbox"
                              data-testid="use-default-notes"
                              className="rounded border-gray-300 w-3.5 h-3.5"
                              checked={formData.notes?.includes(invoiceSettings.defaultNotes)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const existing = formData.notes?.trim();
                                  const defaults = invoiceSettings.defaultNotes;
                                  setFormData(prev => ({
                                    ...prev,
                                    notes: existing ? `${defaults}\n${existing}` : defaults
                                  }));
                                } else {
                                  setFormData(prev => ({
                                    ...prev,
                                    notes: prev.notes?.replace(invoiceSettings.defaultNotes, '').trim()
                                  }));
                                }
                              }}
                            />
                            <span className="text-[10px] text-gray-500 dark:text-muted-foreground">Use Default</span>
                          </label>
                        )}
                      </div>
                      <Textarea
                        value={formData.notes}
                        onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                        rows={4}
                        placeholder="Additional notes for the customer"
                      />
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button type="submit" className="flex-1 bg-teal-600 hover:bg-teal-700">
                      {selectedInvoice ? 'Update Invoice' : 'Create Invoice'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>
      
      {/* Summary Card - Total Only */}
      {summary && (
        <div className="flex justify-end">
          <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 w-full sm:w-auto">
            <CardContent className="p-3 sm:px-6">
              <p className="text-xs text-teal-600">Total Invoices</p>
              <p className="text-lg font-bold text-teal-700">{summary.totalCount || 0}</p>
              <p className="text-xs text-teal-400">{formatCurrency(summary.totalAmount || 0)}</p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search invoice #, customer..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                  data-testid="search-invoices"
                />
              </div>
            </div>
            
            <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Filter className="w-4 h-4 mr-1" />
              Filters
            </Button>
          </div>
          
          {showFilters && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={filterVenue} onValueChange={setFilterVenue}>
                <SelectTrigger>
                  <SelectValue placeholder="Venue" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Venues</SelectItem>
                  {venues.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="From"
              />
              
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="To"
              />
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Bulk Selection Bar */}
      {invoices.length > 0 && (
        <div className="flex items-center justify-between bg-white dark:bg-card rounded-lg border px-4 py-2">
          <div className="flex items-center gap-3">
            <Checkbox
              data-testid="select-all-invoices"
              checked={selectedInvoiceIds.length === invoices.length && invoices.length > 0}
              onCheckedChange={(checked) => {
                setSelectedInvoiceIds(checked ? invoices.map(inv => inv.id) : []);
              }}
            />
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {selectedInvoiceIds.length > 0 
                ? `${selectedInvoiceIds.length} of ${invoices.length} selected`
                : `Select all (${invoices.length})`}
            </span>
          </div>
          {selectedInvoiceIds.length > 0 && (
            <Button 
              size="sm" 
              onClick={bulkDownloadPDF}
              disabled={bulkDownloading}
              data-testid="bulk-download-btn"
              className="bg-teal-600 hover:bg-teal-700 text-white gap-1.5"
            >
              <Download className="w-4 h-4" />
              {bulkDownloading ? 'Generating...' : `Download ${selectedInvoiceIds.length} PDF${selectedInvoiceIds.length > 1 ? 's' : ''}`}
            </Button>
          )}
        </div>
      )}
      
      {/* Invoice List */}
      <div className="space-y-3">
        {loading ? (
          <Card className="p-8 text-center">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto text-teal-600" />
            <p className="mt-2 text-gray-500">Loading invoices...</p>
          </Card>
        ) : invoices.length === 0 ? (
          <Card className="p-8 text-center">
            <Receipt className="w-12 h-12 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500">No invoices found</p>
            <p className="text-sm text-gray-400">Create your first invoice to get started</p>
          </Card>
        ) : (
          invoices.map(invoice => (
            <Card key={invoice.id} className="hover:shadow-md transition-shadow" data-testid={`invoice-${invoice.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                  {/* Checkbox + Invoice Info */}
                  <div className="flex items-start gap-3 flex-1">
                    <Checkbox
                      data-testid={`select-invoice-${invoice.id}`}
                      className="mt-1"
                      checked={selectedInvoiceIds.includes(invoice.id)}
                      onCheckedChange={(checked) => {
                        setSelectedInvoiceIds(prev => 
                          checked ? [...prev, invoice.id] : prev.filter(id => id !== invoice.id)
                        );
                      }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-teal-600">{invoice.invoiceNumber}</span>
                        <Badge className={STATUS_COLORS[invoice.status || 'draft']}>
                          {invoice.status || 'draft'}
                        </Badge>
                      </div>
                      <p className="font-medium mt-1">{invoice.customerName}</p>
                      <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(invoice.invoiceDate)}
                        </span>
                        {invoice.venueName && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {invoice.venueName}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Amount */}
                  <div className="text-right">
                    <p className="text-xl font-bold text-teal-600">
                      {formatCurrency(invoice.grand_total || 0)}
                    </p>
                    {invoice.gstConfig?.enabled && (
                      <p className="text-xs text-gray-500">
                        (incl. GST: {formatCurrency(invoice.total_tax || 0)})
                      </p>
                    )}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => generatePDF(invoice)} title="Download PDF">
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleShare(invoice)} title="Share">
                      <Share2 className="w-4 h-4" />
                    </Button>
                    {canEditInvoices() && (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(invoice)} title="Edit">
                          <Edit className="w-4 h-4" />
                        </Button>
                        {invoice.status === 'draft' && (
                          <Button variant="ghost" size="sm" onClick={() => handleStatusChange(invoice, 'sent')} title="Mark Sent" className="text-blue-600">
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        {(invoice.status === 'sent' || invoice.status === 'draft') && (
                          <Button variant="ghost" size="sm" onClick={() => handleStatusChange(invoice, 'paid')} title="Mark Paid" className="text-green-600">
                            <Check className="w-4 h-4" />
                          </Button>
                        )}
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => { setInvoiceToDelete(invoice); setDeleteConfirmOpen(true); }}
                          title="Delete"
                          className="text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
      
      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Share Invoice</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="text-center py-4">
              <p className="text-sm text-gray-500">Invoice #{shareData.invoice?.invoiceNumber}</p>
              <p className="text-2xl font-bold text-teal-600">
                {formatCurrency(shareData.invoice?.grand_total || 0)}
              </p>
            </div>
            
            {/* Share Link */}
            <div className="flex gap-2">
              <Input value={shareData.link} readOnly className="flex-1" />
              <Button variant="outline" onClick={copyLink}>
                {shareData.copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            
            {/* Share Options */}
            <div className="grid grid-cols-3 gap-3">
              <Button variant="outline" onClick={shareWhatsApp} className="flex flex-col items-center py-4 h-auto">
                <MessageCircle className="w-6 h-6 text-green-600 mb-1" />
                <span className="text-xs">WhatsApp</span>
              </Button>
              <Button variant="outline" onClick={shareEmail} className="flex flex-col items-center py-4 h-auto">
                <Mail className="w-6 h-6 text-blue-600 mb-1" />
                <span className="text-xs">Email</span>
              </Button>
              <Button variant="outline" onClick={() => generatePDF(shareData.invoice)} className="flex flex-col items-center py-4 h-auto">
                <Download className="w-6 h-6 text-teal-600 mb-1" />
                <span className="text-xs">Download</span>
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation */}
      <ConfirmDeleteDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        onConfirm={handleDelete}
        title="Delete Invoice"
        description={`Are you sure you want to delete invoice ${invoiceToDelete?.invoiceNumber}? This action cannot be undone.`}
      />
      
      
      {/* Invoice Settings Dialog */}
      <InvoiceSettingsDialog
        open={settingsDialogOpen}
        onClose={setSettingsDialogOpen}
        invoiceSettings={invoiceSettings}
        setInvoiceSettings={setInvoiceSettings}
        settingsLoading={settingsLoading}
        uploadingField={uploadingField}
        onSave={saveInvoiceSettings}
        onImageSelect={handleSettingsImageSelect}
        onAddCustomUnit={addCustomUnit}
        onRemoveCustomUnit={removeCustomUnit}
        newCustomUnit={newCustomUnit}
        setNewCustomUnit={setNewCustomUnit}
      />

      {/* Image Crop Dialog */}
      <ImageCropDialog
        open={cropDialogOpen}
        onClose={() => { setCropDialogOpen(false); setCropImageSrc(null); setCropCategory(null); }}
        imageSrc={cropImageSrc}
        category={cropCategory}
        onCropComplete={handleCroppedImageUpload}
      />
      </>
      )}
    </div>
  );
};

export default Invoices;
